import { Injectable } from '@angular/core';
import { SessionService } from '@app/security/session.service';
import { Observable, of, throwError } from 'rxjs';
import { ClientService } from '@app/core/services/client.service';
import { Client, ProductGroup } from '@app/core/models/client';
import { TwoWayConfig } from '@app/core/models/two-way-config';
import { map, switchMap, catchError, shareReplay } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@env/environment';

type AutoResponseConfig = {
  message: string;
};

export type AutoResponseType =
  | 'businessHours'
  | 'afterHours'
  | 'outOfOffice';

export const BUSINESS_HOURS: AutoResponseType = 'businessHours';
export const AFTER_HOURS: AutoResponseType = 'afterHours';
export const OUT_OF_OFFICE: AutoResponseType = 'outOfOffice';


export type AutoResponses = Record<AutoResponseType, AutoResponseConfig>;

export interface AutoResponsesPostBody {
  autoResponses: AutoResponses;
}

export interface SupportedLanguages {
  code: string;
  name: string;
}

export interface AutoResponsesFetchResult {
  autoResponses: AutoResponses;
  isFallback: boolean;
}

export interface PingMessages {
  notificationText: string;
  disclaimer: string;
  notificationBody: string; // notificationText + @{auth_link} + disclaimer
  alternateNotificationText: string;
  alternateNotificationTextEnabled: boolean;
}

export interface PingMessagesPostBody {
  pingMessages: Omit<PingMessages, 'notificationBody'>;
}

export interface PingMessagesFetchResult {
  pingMessages: PingMessages;
  isFallback: boolean;
}


export type Day = {
  enabled: boolean;
  startTime: {
    hours: number;
    minutes: number;
  };
  endTime: {
    hours: number;
    minutes: number;
  };
};

export type Timezone = 'EST5EDT' | 'CST6CDT' | 'MST7MDT' | 'PST8PDT';

export interface BusinessHoursResponse {
  businessHours: {
    timezone: Timezone;
    monday: Day;
    tuesday: Day;
    wednesday: Day;
    thursday: Day;
    friday: Day;
    saturday: Day;
    sunday: Day;
    outOfOffice: {
      enabled: boolean;
      expirationDate: Date;
      expirationTime: {
        hours: number;
        minutes: number;
      };
    };
  };
}
export type ClientStatus = {
  type: 'BUSINESS_HOURS' | 'OUT_OF_OFFICE' | 'AFTER_HOURS';
  expires: string;
}
export interface GetClientStatusResponse {
  autoresponse: ClientStatus;
}

@Injectable()
export class TwoWaySetupService {
  private clientStore$: Observable<Client>;
  private defaultProductGroup$: Observable<ProductGroup>;
  private readonly twoWayUrl = `${environment.twoWayURLBase}`;

  constructor(
    private clientService: ClientService,
    private sessionService: SessionService,
    private http: HttpClient,
  ) {
    this.sessionService.selectedClient.subscribe(() => {
      this.resetData();
    });
  }

  resetData() {
    this.clientStore$ = this.refreshClient().pipe(shareReplay(1));
    this.defaultProductGroup$ = this.setDefaultProductGroup().pipe(shareReplay(1));
  }

  /**
   * used for auto-response previews only
   */
  getDefaultProductGroup(): Observable<ProductGroup> {
    return this.defaultProductGroup$;
  }

  /**
   * used to get defaults for after-hours-auto-response blackoutWindows
   */
  getClient(): Observable<Client> {
    return this.clientStore$;
  }

  getTwoWayConfig(): Observable<TwoWayConfig> {
    return this.clientStore$.pipe(
      switchMap((client) => {
        return of(client['two_way_config']);
      }),
    );
  }

  saveTwoWayConfig(newTwoWayConfig: TwoWayConfig): Observable<TwoWayConfig> {
    return this.clientStore$.pipe(
      switchMap((client) => {
        client['two_way_config'] = newTwoWayConfig; // updates client with new two way config
        return this.updateTwoWayConfig(newTwoWayConfig);
      }),
      switchMap((response) => {
        return of(response);
      }),
    );
  }

  /* Auto Responses without translations */
  getAutoResponses(clientId: string): Observable<AutoResponses> {
    const url = `${this.twoWayUrl}/client/${clientId}/configs/autoresponses`;
    return this.http
      .get(url)
      .pipe(map((response) => response['autoResponses']));
  }

  saveAutoResponses(
    clientId: string,
    autoResponsesBody: AutoResponsesPostBody,
  ): Observable<string> {
    const url = `${this.twoWayUrl}/client/${clientId}/configs/autoresponses`;
    return this.http
      .post(url, autoResponsesBody)
      .pipe(map((response) => response['message']));
  }

  /* Multilingual Auto Responses */
  getAutoResponsesWithLanguages(clientId: string, language: string): Observable<AutoResponsesFetchResult> {
    const url = `${this.twoWayUrl}/client/${clientId}/configs/autoresponses/translations/${language}`;
    return this.http
      .get(url)
      .pipe(
        map((response) => ({
          autoResponses: response['autoResponses'],
          isFallback: false,
        })),
        catchError((error: HttpErrorResponse) => {
          if (this.isAutoResponseNotFoundError(error)) {
            return of({
              autoResponses: this.createEmptyAutoResponses(),
              isFallback: true,
            });
          }
          return throwError(() => error);
        }),
      );
  }

  saveAutoResponsesWithLanguages(
    clientId: string,
    autoResponsesBody: AutoResponsesPostBody,
    language: string
  ): Observable<string> {
    const url = `${this.twoWayUrl}/client/${clientId}/configs/autoresponses/translations/${language}`;
    return this.http
      .post(url, autoResponsesBody)
      .pipe(map((response) => response['message']));
  }

  getSupportedLanguages(clientId: string): Observable<SupportedLanguages[]> {
    const url = `${this.twoWayUrl}/client/${clientId}/configs/translations/supported_languages`;
    return this.http.get<{ supportedLanguages: SupportedLanguages[] }>(url).pipe(
      map((response) => response.supportedLanguages)
    );
  }

  /* Multilingual Ping Messages */
  getPingMessagesWithLanguages(clientId: string, language: string): Observable<PingMessagesFetchResult> {
    const url = `${this.twoWayUrl}/client/${clientId}/configs/pingmessages/translations/${language}`;
    return this.http
      .get<{ pingMessages: PingMessages }>(url)
      .pipe(
        map((response) => ({
          pingMessages: response.pingMessages,
          isFallback: false,
        })),
        catchError((error: HttpErrorResponse) => {
          if (this.isPingMessagesNotFoundError(error)) {
            return of({
              pingMessages: this.createEmptyPingMessages(),
              isFallback: true,
            });
          }
          return throwError(() => error);
        }),
      );
  }

  savePingMessagesWithLanguages(
    clientId: string,
    pingMessagesBody: PingMessagesPostBody,
    language: string
  ): Observable<string> {
    const url = `${this.twoWayUrl}/client/${clientId}/configs/pingmessages/translations/${language}`;
    return this.http
      .post<{ message: string }>(url, pingMessagesBody)
      .pipe(map((response) => response['message']));
  }

  getBusinessHours(clientId: string) {
    const url = `${this.twoWayUrl}/client/${clientId}/configs/businesshours`;
    return this.http.get<BusinessHoursResponse>(url).pipe(
      map((response) => {
        return response['businessHours'];
      }),
    );
  }

  getClientStatus(clientId: string) {
    const url = `${this.twoWayUrl}/client/${clientId}/status`;
    return this.http.get<GetClientStatusResponse>(url).pipe(
      map((response) => {
        return response['autoresponse'];
      }),
    );
  }

  private refreshClient(): Observable<Client> {
    return this.clientService.getClient(this.clientId, true).pipe(
      switchMap((response: Client) => {
        return of(response);
      }),
    );
  }

  private setDefaultProductGroup(): Observable<ProductGroup> {
    return this.clientStore$.pipe(
      switchMap((client) => {
        return this.clientService.getProductGroup(client.id, 'default', true);
      }),
    );
  }

  private updateTwoWayConfig(
    TwoWayConfig: TwoWayConfig,
  ): Observable<TwoWayConfig> {
    return this.clientService.updateClientPartial(
      this.clientId,
      'two_way_config',
      TwoWayConfig,
    );
  }

  updateBusinessHours(
    clientId: string,
    updatedBusinessHours: BusinessHoursResponse['businessHours'],
  ): Observable<string> {
    const body = { businessHours: updatedBusinessHours };
    body.businessHours.outOfOffice = {
      enabled: updatedBusinessHours.outOfOffice.enabled,
      expirationDate: updatedBusinessHours.outOfOffice.expirationDate,
      expirationTime: {
        hours: updatedBusinessHours.outOfOffice.expirationTime.hours,
        minutes: updatedBusinessHours.outOfOffice.expirationTime.minutes,
      },
    };
    const url = `${this.twoWayUrl}/client/${clientId}/configs/businesshours`;
    return this.http.post(url, body).pipe(
      map((response) => {
        return response['message'];
      }),
    );
  }

  private get clientId(): string {
    return this.sessionService.getCurrentUsersClient().id;
  }

  private createEmptyAutoResponses(): AutoResponses {
    return {
      businessHours: { message: '' },
      afterHours: { message: '' },
      outOfOffice: { message: '' },
    };
  }

  private isAutoResponseNotFoundError(error: HttpErrorResponse): boolean {
    if (error.status !== 404) {
      return false;
    }
    const message =
      typeof error.error === 'string'
        ? error.error
        : error.error?.message ?? error.message;
    return (
      typeof message === 'string' &&
      message.toLowerCase().includes('auto response not found')
    );
  }

  private createEmptyPingMessages(): PingMessages {
    return {
      notificationText: '',
      disclaimer: '',
      notificationBody: '',
      alternateNotificationText: '',
      alternateNotificationTextEnabled: false,
    };
  }

  private isPingMessagesNotFoundError(error: HttpErrorResponse): boolean {
    if (error.status !== 404) {
      return false;
    }
    const message =
      typeof error.error === 'string'
        ? error.error
        : error.error?.message ?? error.message;
    return (
      typeof message === 'string' &&
      message.toLowerCase().includes('ping notification not found')
    );
  }
}
